import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Building2, Upload, CheckCircle2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PublicIssue() {
  const { token } = useParams();
  const [info, setInfo] = useState(undefined); // undefined=loading, null=invalid
  const [form, setForm] = useState({ reporter_name: "", contact: "", description: "", photos: [] });
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);

  useEffect(() => {
    axios.get(`${API}/public/report/${token}`).then((r) => setInfo(r.data)).catch(() => setInfo(null));
  }, [token]);

  const onPhoto = (e) => {
    const files = [...e.target.files].slice(0, 6);
    Promise.all(files.map((f) => new Promise((res) => { const r = new FileReader(); r.onload = () => res(r.result); r.readAsDataURL(f); })))
      .then((arr) => setForm((p) => ({ ...p, photos: [...p.photos, ...arr].slice(0, 6) })));
  };

  const submit = async () => {
    setSending(true); setErr("");
    try { await axios.post(`${API}/public/report/${token}`, form); setDone(true); }
    catch (e) {
      setSending(false);
      setErr(e.response?.status === 429
        ? (e.response?.data?.detail || "Too many reports just now — please wait a few minutes and try again.")
        : "Sorry, we couldn't send your report just now. Please try again.");
    }
  };

  if (info === undefined) return <div className="min-h-screen flex items-center justify-center bg-[#FDFBF7]"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  if (info === null) return <div className="min-h-screen flex items-center justify-center bg-[#FDFBF7] p-6"><p className="text-center text-muted-foreground">This report link is invalid or has expired.</p></div>;

  return (
    <div className="min-h-screen bg-[#FDFBF7] flex flex-col items-center py-10 px-4">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-2 mb-6">
          <div className="h-9 w-9 rounded-md bg-[#18181A] flex items-center justify-center"><Building2 className="h-5 w-5 text-white" /></div>
          <span className="font-head text-xl font-extrabold tracking-tight">{info.brand_name}</span>
        </div>
        {done ? (
          <div className="bg-white border border-border rounded-md p-8 text-center" data-testid="public-thanks">
            <CheckCircle2 className="h-10 w-10 text-[#2B4C3F] mx-auto mb-3" />
            <h1 className="font-head text-2xl font-extrabold">Thank you</h1>
            <p className="text-sm text-muted-foreground mt-2">Your report for <strong>{info.property_title}</strong> has been sent to our team. We'll be in touch if we need more details.</p>
          </div>
        ) : (
          <div className="bg-white border border-border rounded-md p-6">
            <p className="overline text-accent">Report an issue</p>
            <h1 className="font-head text-2xl font-extrabold tracking-tight mt-1">{info.property_title}</h1>
            <p className="text-sm text-muted-foreground mt-1 mb-5">Staying here and something's not right? Let us know and we'll sort it.</p>
            {(info.guestbook?.welcome_note || info.guestbook?.wifi_name || info.guestbook?.checkout_time || info.guestbook?.house_rules) && (
              <div data-testid="guestbook" className="bg-secondary/40 border border-border rounded-md p-4 mb-5 text-sm space-y-1.5">
                {info.guestbook.welcome_note && <p>{info.guestbook.welcome_note}</p>}
                {info.guestbook.wifi_name && <p><span className="overline text-muted-foreground">WiFi</span> {info.guestbook.wifi_name}{info.guestbook.wifi_password ? ` · ${info.guestbook.wifi_password}` : ""}</p>}
                {info.guestbook.checkout_time && <p><span className="overline text-muted-foreground">Check-out</span> {info.guestbook.checkout_time}</p>}
                {info.guestbook.house_rules && <p className="whitespace-pre-wrap"><span className="overline text-muted-foreground">House rules</span> {info.guestbook.house_rules}</p>}
              </div>
            )}
            <div className="space-y-3">
              <div><Label className="overline">Your name</Label><Input data-testid="pub-name" value={form.reporter_name} onChange={(e) => setForm({ ...form, reporter_name: e.target.value })} className="mt-1.5" /></div>
              <div><Label className="overline">Phone or email (optional)</Label><Input data-testid="pub-contact" value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })} className="mt-1.5" /></div>
              <div><Label className="overline">What's the issue?</Label><Textarea data-testid="pub-desc" rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1.5" /></div>
              <div>
                <Label className="overline">Photos (optional)</Label>
                <label className="mt-1.5 flex items-center gap-2 text-sm underline cursor-pointer"><Upload className="h-4 w-4" />Add photos<input type="file" accept="image/*" multiple className="hidden" data-testid="pub-photos" onChange={onPhoto} /></label>
                {form.photos.length > 0 && <div className="flex gap-2 flex-wrap mt-2">{form.photos.map((p, i) => <img key={i} src={p} alt="" className="h-16 w-16 object-cover rounded-md border border-border" />)}</div>}
              </div>
              <Button data-testid="pub-submit" className="w-full" disabled={sending || !form.reporter_name || !form.description} onClick={submit}>
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send report"}
              </Button>
              {err && <p data-testid="pub-error" className="text-xs text-[#9E382A] text-center">{err}</p>}
            </div>
          </div>
        )}
        <p className="text-center text-xs text-muted-foreground mt-4">Powered by {info.brand_name}</p>
      </div>
    </div>
  );
}
